import { FC, useMemo } from "react";
import { Box, Stack } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { Table } from "@/components/elements/Table";
import { Input } from "@/components/elements/Input/Input";
import { useTable } from "@/hooks/useTable";
import { IconContentCopy, IconFilterList } from "@/assets/icons";
import { black, icon, divider } from "@/theme/colors";
import { COPY_SUCCESS_MESSAGE } from "@/constants/common";
import { toast } from "@/utils/toast";
import { TBulkAccountRow } from "@/features/learner/components/learner-bulk-import/bulk-import-mock";

type Props = {
  rows: TBulkAccountRow[];
};

// ソート表示のみ (実ソートは未実装)
const SortHeader = ({ label }: { label: string }) => (
  <Stack direction="row" alignItems="center" gap={0.75}>
    <span>{label}</span>
    <IconFilterList fontSize={16} color={black[80]} />
  </Stack>
);

// 編集フィールドを列幅いっぱいに広げる
const FieldCell: FC<{ children: React.ReactNode }> = ({ children }) => <Box sx={{ width: "100%", minWidth: 0 }}>{children}</Box>;

// 発行済みの認証情報。閲覧専用(readonly)で列幅いっぱいに表示する
const ReadonlyCredential: FC<{ value: string }> = ({ value }) => (
  <FieldCell>
    <Input readonly clearable={false} value={value} style={{ width: "100%" }} />
  </FieldCell>
);

export const BulkImportCompleteTable: FC<Props> = ({ rows }) => {
  const handleCopyRow = (row: TBulkAccountRow) => {
    const loginInfo = [`ログインURL:\n${row.loginUrl}`, `学習者ID:\n${row.learnerId}`, `パスワード:\n${row.password}`].join("\n\n");
    navigator.clipboard.writeText(loginInfo);
    toast.success(COPY_SUCCESS_MESSAGE);
  };

  const columns = useMemo<MRT_ColumnDef<TBulkAccountRow>[]>(
    () => [
      { header: "氏名", id: "fullName", size: 170, grow: false, Header: <SortHeader label="氏名" />, Cell: ({ row }) => row.original.fullName },
      { header: "所属機関", id: "companyName", size: 180, grow: false, Header: <SortHeader label="所属機関" />, Cell: ({ row }) => row.original.companyName || "—" },
      { header: "受験分野", id: "examField", size: 120, grow: false, Header: <SortHeader label="受験分野" />, Cell: ({ row }) => row.original.examFieldLabel || "—" },
      { header: "国籍", id: "nationality", size: 120, grow: false, Header: <SortHeader label="国籍" />, Cell: ({ row }) => row.original.nationality },
      { header: "性別", id: "gender", size: 80, grow: false, Header: <SortHeader label="性別" />, Cell: ({ row }) => row.original.gender },
      // ログインURL のみ伸縮させ、余白を吸収して URL を全幅で表示する
      { header: "ログインURL", id: "loginUrl", size: 220, grow: true, Header: <SortHeader label="ログインURL" />, Cell: ({ row }) => <ReadonlyCredential value={row.original.loginUrl} /> },
      { header: "学習者ID", id: "learnerId", size: 200, grow: false, Header: <SortHeader label="学習者ID" />, Cell: ({ row }) => <ReadonlyCredential value={row.original.learnerId} /> },
      { header: "パスワード", id: "password", size: 160, grow: false, Header: <SortHeader label="パスワード" />, Cell: ({ row }) => <ReadonlyCredential value={row.original.password} /> },
      {
        header: "",
        id: "copy",
        size: 56,
        // grid モードでも伸ばさず 56px 固定にする
        grow: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <Stack
            alignItems="center"
            justifyContent="center"
            onClick={() => handleCopyRow(row.original)}
            sx={{ width: 32, height: 32, mx: "auto", cursor: "pointer", color: icon.primary, "&:hover": { opacity: 0.7 } }}
          >
            <IconContentCopy width={18} height={18} />
          </Stack>
        ),
      },
    ],
    [],
  );

  const { table } = useTable({
    columns,
    rows,
    getRowId: (row) => row.id.toString(),
    // コピー列を右端に固定
    pinningColumns: ["copy"],
    // grid: 列を伸ばしてテーブル幅いっぱいに広げる (列数が少なく右側に余白が出ないように)
    layoutMode: "grid",
    enableRowSelection: false,
    defaultColumn: { muiTableBodyCellProps: { sx: { minWidth: 0, overflow: "hidden" } } },
    renderEmptyRowsFallback: () => <Box />,
  });

  // 高さ指定なし → コンテナは内容の高さ。最終行が枠線に密着し、下に余白が出ない
  // 入力欄(readonly)があるため、行高は編集テーブルと揃えて 53px にする
  return <Table table={table} isLoading={false} bodyRowHeight={53} borderColor={divider.high} />;
};
